import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faPlus, faClock, faTrash, faVideo, faHospital } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import ProviderShell, { useProviderApi } from './layouts/ProviderShell';
import { getSpecialtyVisitTitles } from './specialtyFields';

interface Appointment {
    id: number;
    title: string;
    appointment_type: 'in_person' | 'telehealth';
    status: 'scheduled' | 'confirmed' | 'completed' | 'cancelled' | 'no_show';
    start_datetime: string;
    end_datetime: string;
    location: string;
    notes: string;
    patient: { patient_id: string; name: string };
}

interface PatientOption {
    patient_id: string;
    first_name: string;
    last_name: string;
}

interface AvailabilitySlot {
    id: number;
    weekday: number;
    weekday_label: string;
    start_time: string;
    end_time: string;
    active: boolean;
}

const WEEKDAYS = [
    { value: 0, label: 'Monday' }, { value: 1, label: 'Tuesday' }, { value: 2, label: 'Wednesday' },
    { value: 3, label: 'Thursday' }, { value: 4, label: 'Friday' }, { value: 5, label: 'Saturday' }, { value: 6, label: 'Sunday' },
];

const STATUS_COLORS: Record<string, string> = {
    scheduled: 'warning', confirmed: 'info', completed: 'success', cancelled: 'secondary', no_show: 'danger',
};

const ProviderAppointments: React.FC = () => {
    const { api } = useProviderApi();
    const [tab, setTab] = useState<'appointments' | 'availability'>('appointments');

    const [appointments, setAppointments] = useState<Appointment[]>([]);
    const [patients, setPatients] = useState<PatientOption[]>([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('');

    const [showCreate, setShowCreate] = useState(false);
    const [form, setForm] = useState({ patient_id: '', title: '', appointment_type: 'in_person', start_datetime: '', end_datetime: '', location: '', notes: '' });
    const [submitting, setSubmitting] = useState(false);

    const [slots, setSlots] = useState<AvailabilitySlot[]>([]);
    const [slotForm, setSlotForm] = useState({ weekday: 0, start_time: '09:00', end_time: '17:00' });

    // Specialty-contextual visit title suggestions (tracker 9.9) - a
    // <datalist>, not a constraint, so the title field stays free text.
    const [specialization, setSpecialization] = useState<string | null>(null);
    const visitTitleSuggestions = getSpecialtyVisitTitles(specialization);

    useEffect(() => { document.title = 'Appointments - Uzima Healthcare'; }, []);

    useEffect(() => {
        api.get('practitioners/me/').then(res => {
            if (res.data?.status === 'success') setSpecialization(res.data.data?.specialization || null);
        }).catch(() => { /* title suggestions just stay empty */ });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadAppointments = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (statusFilter) params.set('status', statusFilter);
            const res = await api.get(`practitioners/appointments/?${params}`);
            if (res.data?.status === 'success') setAppointments(res.data.data || []);
        } catch { setAppointments([]); } finally { setLoading(false); }
    };

    const loadPatients = async () => {
        try {
            const res = await api.get('practitioners/patients/?status=active');
            if (res.data?.status === 'success') setPatients(res.data.data.map((p: any) => ({ patient_id: p.patient_id, first_name: p.first_name, last_name: p.last_name })));
        } catch { /* patient select stays empty on failure */ }
    };

    const loadSlots = async () => {
        try {
            const res = await api.get('practitioners/availability/');
            if (res.data?.status === 'success') setSlots(res.data.data || []);
        } catch { setSlots([]); }
    };

    useEffect(() => { loadAppointments(); loadPatients(); loadSlots(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { loadAppointments(); }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    const submitAppointment = async () => {
        if (!form.patient_id || !form.title.trim() || !form.start_datetime || !form.end_datetime) {
            Swal.fire({ icon: 'warning', title: 'Missing Fields', text: 'Patient, title, start and end time are required.' });
            return;
        }
        setSubmitting(true);
        try {
            const res = await api.post('practitioners/appointments/', form);
            if (res.data?.status === 'success') {
                Swal.fire({ icon: 'success', title: 'Appointment Scheduled', timer: 1500, showConfirmButton: false });
                setShowCreate(false);
                setForm({ patient_id: '', title: '', appointment_type: 'in_person', start_datetime: '', end_datetime: '', location: '', notes: '' });
                loadAppointments();
            }
        } catch (err: any) {
            Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.message || 'Failed to schedule appointment.' });
        } finally {
            setSubmitting(false);
        }
    };

    const updateStatus = async (apt: Appointment, status: string) => {
        try {
            await api.patch(`practitioners/appointments/${apt.id}/`, { status });
            loadAppointments();
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to update appointment.' });
        }
    };

    const cancelAppointment = async (apt: Appointment) => {
        const confirm = await Swal.fire({ title: 'Cancel this appointment?', icon: 'warning', showCancelButton: true, confirmButtonColor: '#dc3545' });
        if (!confirm.isConfirmed) return;
        try {
            await api.delete(`practitioners/appointments/${apt.id}/`);
            loadAppointments();
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to cancel appointment.' });
        }
    };

    const addSlot = async () => {
        try {
            const res = await api.post('practitioners/availability/', slotForm);
            if (res.data?.status === 'success') {
                loadSlots();
            }
        } catch (err: any) {
            Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.message || 'Failed to add availability.' });
        }
    };

    const removeSlot = async (slot: AvailabilitySlot) => {
        try {
            await api.delete(`practitioners/availability/${slot.id}/`);
            loadSlots();
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to remove availability.' });
        }
    };

    return (
        <ProviderShell>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="fw-bold mb-1"><FontAwesomeIcon icon={faCalendarAlt} className="me-2 text-primary" />Appointments</h2>
                    <p className="text-muted mb-0">Your schedule and availability</p>
                </div>
                {tab === 'appointments' && (
                    <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
                        <FontAwesomeIcon icon={faPlus} className="me-2" />Schedule Appointment
                    </button>
                )}
            </div>

            <ul className="nav nav-pills mb-4">
                <li className="nav-item"><button className={`nav-link ${tab === 'appointments' ? 'active' : ''}`} onClick={() => setTab('appointments')}>Appointments</button></li>
                <li className="nav-item"><button className={`nav-link ${tab === 'availability' ? 'active' : ''}`} onClick={() => setTab('availability')}>Availability</button></li>
            </ul>

            {tab === 'appointments' && (
                <>
                    <div className="mb-3">
                        <select className="form-select" style={{ maxWidth: 220 }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                            <option value="">All Statuses</option>
                            <option value="scheduled">Scheduled</option>
                            <option value="confirmed">Confirmed</option>
                            <option value="completed">Completed</option>
                            <option value="cancelled">Cancelled</option>
                            <option value="no_show">No Show</option>
                        </select>
                    </div>

                    <div className="card">
                        <div className="card-body p-0">
                            {loading ? (
                                <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
                            ) : appointments.length === 0 ? (
                                <div className="text-center py-5">
                                    <FontAwesomeIcon icon={faCalendarAlt} className="text-muted mb-3" style={{ fontSize: '2.5rem' }} />
                                    <h5>No appointments yet</h5>
                                    <p className="text-muted">Schedule your first appointment with a patient from your roster.</p>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-hover align-middle mb-0">
                                        <thead className="table-light">
                                            <tr><th>Patient</th><th>Title</th><th>Type</th><th>When</th><th>Status</th><th>Action</th></tr>
                                        </thead>
                                        <tbody>
                                            {appointments.map(a => (
                                                <tr key={a.id}>
                                                    <td>{a.patient.name}</td>
                                                    <td>{a.title}</td>
                                                    <td><FontAwesomeIcon icon={a.appointment_type === 'telehealth' ? faVideo : faHospital} className="me-1 text-muted" />{a.appointment_type === 'telehealth' ? 'Telehealth' : 'In Person'}</td>
                                                    <td>{new Date(a.start_datetime).toLocaleString()}</td>
                                                    <td><span className={`badge bg-${STATUS_COLORS[a.status]}`}>{a.status.replace('_', ' ')}</span></td>
                                                    <td>
                                                        <div className="d-flex gap-1">
                                                            {a.status === 'scheduled' && (
                                                                <button className="btn btn-sm btn-outline-info" onClick={() => updateStatus(a, 'confirmed')}>Confirm</button>
                                                            )}
                                                            {(a.status === 'scheduled' || a.status === 'confirmed') && (
                                                                <button className="btn btn-sm btn-outline-success" onClick={() => updateStatus(a, 'completed')}>Complete</button>
                                                            )}
                                                            <button className="btn btn-sm btn-outline-danger" onClick={() => cancelAppointment(a)}><FontAwesomeIcon icon={faTrash} /></button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}

            {tab === 'availability' && (
                <div className="row">
                    <div className="col-lg-5 mb-4">
                        <div className="card">
                            <div className="card-header"><strong>Add Availability</strong></div>
                            <div className="card-body">
                                <div className="mb-3">
                                    <label className="form-label">Day</label>
                                    <select className="form-select" value={slotForm.weekday} onChange={(e) => setSlotForm(f => ({ ...f, weekday: Number(e.target.value) }))}>
                                        {WEEKDAYS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                                    </select>
                                </div>
                                <div className="row g-3 mb-3">
                                    <div className="col-6">
                                        <label className="form-label">Start Time</label>
                                        <input type="time" className="form-control" value={slotForm.start_time} onChange={(e) => setSlotForm(f => ({ ...f, start_time: e.target.value }))} />
                                    </div>
                                    <div className="col-6">
                                        <label className="form-label">End Time</label>
                                        <input type="time" className="form-control" value={slotForm.end_time} onChange={(e) => setSlotForm(f => ({ ...f, end_time: e.target.value }))} />
                                    </div>
                                </div>
                                <button className="btn btn-primary w-100" onClick={addSlot}>
                                    <FontAwesomeIcon icon={faClock} className="me-2" />Add Slot
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-7 mb-4">
                        <div className="card">
                            <div className="card-header"><strong>Your Weekly Availability</strong></div>
                            <div className="card-body p-0">
                                {slots.length === 0 ? (
                                    <div className="text-center py-5 text-muted">No availability windows set yet.</div>
                                ) : (
                                    <div className="table-responsive">
                                        <table className="table table-hover align-middle mb-0">
                                            <thead className="table-light"><tr><th>Day</th><th>Time</th><th></th></tr></thead>
                                            <tbody>
                                                {slots.map(s => (
                                                    <tr key={s.id}>
                                                        <td>{s.weekday_label}</td>
                                                        <td>{s.start_time} - {s.end_time}</td>
                                                        <td className="text-end">
                                                            <button className="btn btn-sm btn-outline-danger" onClick={() => removeSlot(s)}><FontAwesomeIcon icon={faTrash} /></button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showCreate && (
                <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Schedule Appointment</h5>
                                <button type="button" className="btn-close" onClick={() => setShowCreate(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Patient</label>
                                    <select className="form-select" value={form.patient_id} onChange={(e) => setForm(f => ({ ...f, patient_id: e.target.value }))}>
                                        <option value="">Select a patient...</option>
                                        {patients.map(p => <option key={p.patient_id} value={p.patient_id}>{p.first_name} {p.last_name}</option>)}
                                    </select>
                                    {patients.length === 0 && <div className="form-text text-warning">No patients on your roster yet — add one under My Patients first.</div>}
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Title</label>
                                    <input type="text" className="form-control" list="visit-title-suggestions" placeholder="e.g. Follow-up consultation" value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} />
                                    {visitTitleSuggestions.length > 0 && (
                                        <datalist id="visit-title-suggestions">
                                            {visitTitleSuggestions.map(t => <option key={t} value={t} />)}
                                        </datalist>
                                    )}
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Type</label>
                                    <select className="form-select" value={form.appointment_type} onChange={(e) => setForm(f => ({ ...f, appointment_type: e.target.value }))}>
                                        <option value="in_person">In Person</option>
                                        <option value="telehealth">Telehealth</option>
                                    </select>
                                </div>
                                <div className="row g-3 mb-3">
                                    <div className="col-md-6">
                                        <label className="form-label">Start</label>
                                        <input type="datetime-local" className="form-control" value={form.start_datetime} onChange={(e) => setForm(f => ({ ...f, start_datetime: e.target.value }))} />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">End</label>
                                        <input type="datetime-local" className="form-control" value={form.end_datetime} onChange={(e) => setForm(f => ({ ...f, end_datetime: e.target.value }))} />
                                    </div>
                                </div>
                                {form.appointment_type === 'in_person' && (
                                    <div className="mb-3">
                                        <label className="form-label">Location</label>
                                        <input type="text" className="form-control" value={form.location} onChange={(e) => setForm(f => ({ ...f, location: e.target.value }))} />
                                    </div>
                                )}
                                <div className="mb-3">
                                    <label className="form-label">Notes</label>
                                    <textarea className="form-control" rows={2} value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-light" onClick={() => setShowCreate(false)}>Cancel</button>
                                <button className="btn btn-primary" disabled={submitting} onClick={submitAppointment}>{submitting ? 'Scheduling...' : 'Schedule'}</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </ProviderShell>
    );
};

export default ProviderAppointments;
